import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import {
  FileCopy as CopyIcon,
  Share as ShareIcon,
  GetApp as DownloadIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import AffiliateLayout from '../../components/affiliate/AffiliateLayoutSimple';
import AffiliateStats from '../../components/affiliate/AffiliateStats';
import ConversionsTable from '../../components/affiliate/ConversionsTable';
import { useQuery, useMutation } from '@apollo/client';
import { CURRENT_AFFILIATE_QUERY } from '../../core/graphql/queries/affiliate';
import { APPLY_FOR_AFFILIATE } from '../../core/graphql/mutations/affiliate';

const useStyles = makeStyles((theme) => ({
  welcomeCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  welcomeContent: {
    padding: theme.spacing(3),
  },
  welcomeTitle: {
    color: '#fff',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  welcomeSubtitle: {
    color: '#ccc',
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    marginBottom: theme.spacing(2),
  },
  referralCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  referralContent: {
    padding: theme.spacing(3),
  },
  referralInput: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d4a3e',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#4caf50',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4caf50',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#ccc',
    },
    '& .MuiOutlinedInput-input': {
      color: '#fff',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  recentConversionsCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  recentConversionsContent: {
    padding: theme.spacing(3),
  },
  quickActionsCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  quickActionsContent: {
    padding: theme.spacing(3),
  },
  actionGrid: {
    marginTop: theme.spacing(2),
  },
  actionButton: {
    backgroundColor: '#2d4a3e',
    border: '1px solid #4caf50',
    color: '#4caf50',
    '&:hover': {
      backgroundColor: '#4caf50',
      color: '#fff',
    },
  },
}));

const AffiliateDashboard: React.FC = () => {
  const classes = useStyles();
  const [referralLink, setReferralLink] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { data, loading, error } = useQuery(CURRENT_AFFILIATE_QUERY, {
    errorPolicy: 'all',
  });

  const [applyForAffiliate, { loading: applying }] = useMutation(APPLY_FOR_AFFILIATE, {
    refetchQueries: [{ query: CURRENT_AFFILIATE_QUERY }],
    onCompleted: (data) => {
      setSnackbarMessage('Affiliate application submitted successfully! You will receive an email confirmation shortly.');
      setSnackbarOpen(true);
    },
    onError: (error) => {
      setSnackbarMessage(`Application failed: ${error.message}`);
      setSnackbarOpen(true);
    }
  });

  const affiliate = data?.myAffiliate;
  const isAffiliateApproved =
    affiliate?.status === 'active' || affiliate?.status === 'approved';

  // Handler function for applying to become an affiliate
  const handleApplyForAffiliate = async () => {
    try {
      await applyForAffiliate({
        variables: {
          applicationNotes: 'Application submitted via dashboard'
        }
      });
    } catch (error) {
      console.error('Error applying for affiliate:', error);
    }
  };

  // Set referral link when affiliate data is available
  useEffect(() => {
    if (affiliate?.referralCode) {
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/signup?ref=${affiliate.referralCode}`);
    }
  }, [affiliate]);

  // Handle loading state
  if (loading) {
    return (
      <AffiliateLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" style={{ color: '#fff' }}>
            Loading affiliate dashboard...
          </Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  // Handle error state or no affiliate - show application form
  if (error || !affiliate) {
    return (
      <AffiliateLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Card style={{ backgroundColor: '#1a2332', border: '1px solid #333', padding: '2rem', textAlign: 'center', maxWidth: '500px' }}>
            <Typography variant="h5" style={{ color: '#fff', marginBottom: '1rem' }}>
              Become an Affiliate
            </Typography>
            <Typography style={{ color: '#ccc', marginBottom: '2rem' }}>
              Join our affiliate program and start earning commissions by referring artists to StarFarm.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyForAffiliate}
              disabled={applying}
              style={{ marginRight: '1rem' }}
            >
              {applying ? 'Applying...' : 'Apply Now'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => window.history.back()}
            >
              Go Back
            </Button>
          </Card>
        </Box>
      </AffiliateLayout>
    );
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setSnackbarMessage('Referral link copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to copy link');
      setSnackbarOpen(true);
    }
  };

  const handleShareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join StarFarm as an Artist',
          text: 'Join me on StarFarm and start earning from your music!',
          url: referralLink,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      handleCopyLink();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'approved':
        return '#4caf50';
      case 'pending':
        return '#ff9800';
      case 'suspended':
        return '#f44336';
      default:
        return '#ccc';
    }
  };

  if (loading) {
    return (
      <AffiliateLayout title="Affiliate Dashboard">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography style={{ color: '#fff' }}>Loading...</Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  if (error) {
    return (
      <AffiliateLayout title="Affiliate Dashboard">
        <Box textAlign="center" padding={4}>
          <Typography variant="h6" style={{ color: '#f44336' }} gutterBottom>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2" style={{ color: '#ccc' }}>
            Unable to load affiliate information. Please try again later.
          </Typography>
        </Box>
      </AffiliateLayout>
    );
  }

  return (
    <AffiliateLayout
      title="Affiliate Dashboard"
      subtitle="Welcome back! Here's your affiliate performance overview."
    >
      {/* Welcome Section */}
      <Card className={classes.welcomeCard}>
        <CardContent className={classes.welcomeContent}>
          <Typography variant="h5" className={classes.welcomeTitle}>
            Welcome back, {affiliate?.name || 'Affiliate'}!
          </Typography>
          <Typography variant="body1" className={classes.welcomeSubtitle}>
            Your affiliate account is{' '}
            <span style={{ color: getStatusColor(affiliate?.status || 'pending') }}>
              {affiliate?.status || 'pending'}
            </span>
            {isAffiliateApproved && '. You can start earning commissions!'}
            {affiliate?.status === 'pending' && '. Your account is under review.'}
            {affiliate?.status === 'suspended' && '. Please contact support.'}
          </Typography>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <AffiliateStats />

      {/* Referral Link Management */}
      {isAffiliateApproved && (
        <Card className={classes.referralCard}>
          <CardContent className={classes.referralContent}>
            <Typography variant="h6" style={{ color: '#fff', marginBottom: 16 }}>
              Your Referral Link
            </Typography>
            <TextField
              fullWidth
              value={referralLink}
              label="Referral Link"
              className={classes.referralInput}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} style={{ color: '#4caf50' }}>
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box className={classes.actionButtons}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyLink}
                className={classes.actionButton}
              >
                Copy Link
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShareLink}
                className={classes.actionButton}
              >
                Share
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Recent Conversions */}
      <Card className={classes.recentConversionsCard}>
        <CardContent className={classes.recentConversionsContent}>
          <Typography variant="h6" style={{ color: '#fff', marginBottom: 16 }}>
            Recent Conversions
          </Typography>
          <ConversionsTable />
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className={classes.quickActionsCard}>
        <CardContent className={classes.quickActionsContent}>
          <Typography variant="h6" style={{ color: '#fff', marginBottom: 16 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2} className={classes.actionGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.actionButton}
                href="/affiliate/conversions"
              >
                View All Conversions
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.actionButton}
                href="/affiliate/profile"
              >
                Update Profile
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                className={classes.actionButton}
                href="/affiliate/payouts"
              >
                View Payouts
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                className={classes.actionButton}
              >
                Export Data
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <div style={{ 
          backgroundColor: '#4caf50', 
          color: 'white', 
          padding: '12px 16px', 
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{snackbarMessage}</span>
          <button 
            onClick={() => setSnackbarOpen(false)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'white', 
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      </Snackbar>
    </AffiliateLayout>
  );
};

export default AffiliateDashboard;
